import React, { useState, FC, ChangeEvent, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../components/ui/Button";
import SearchBar from "../../components/ui/SearchBar";
import Badge from "../../components/ui/Badge";
import { Job, JobFilterOptions, SelectedFilters } from "../../types/components";
import jobService from "../../services/job.service";
import { JobApplicationStatus, JobPostResponse, UserJobApplicationResponse } from "../../types/api";

type JobWithApplication = JobPostResponse & {
  applicationStatus?: JobApplicationStatus;
  applicationId?: number;
};

const UserJobs: FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    type: "all",
    location: "all",
    experience: "all",
    remote: "all",
  });
  const [jobsWithApplications, setJobsWithApplications] = useState<
    JobWithApplication[]
  >([]);

  useEffect(() => {
    if (user) {
      const fetchJobsAndApplications = async () => {
        try {
          const [jobsResponse, applicationsResponse] = await Promise.all([
            jobService.getAllPosts(1, 100), // Assuming we fetch up to 100 jobs
            jobService.getUserApplications(user.userId, 1, 100), // And up to 100 applications
          ]);

          const jobs = jobsResponse.items || [];
          const applications = applicationsResponse.data.items || [];

          const applicationsMap = new Map<number, UserJobApplicationResponse>();
          applications.forEach((app) => {
            if (app.postId) {
              applicationsMap.set(app.postId, app);
            }
          });

          const mergedJobs: JobWithApplication[] = jobs.map((job) => {
            const application = applicationsMap.get(job.postId);
            return {
              ...job,
              applicationStatus: application?.status,
              applicationId: application?.applicationId,
            };
          });

          setJobsWithApplications(mergedJobs);
        } catch (error) {
          console.error("Error fetching jobs or applications:", error);
        }
      };

      fetchJobsAndApplications();
    }
  }, [user]);

  const handleApplicationStatusChange = async (
    job: JobWithApplication,
    newStatus: JobApplicationStatus
  ) => {
    if (!user) return;

    try {
      if (job.applicationId) {
        // Update existing application
        await jobService.updateJobApplication({
          applicationId: job.applicationId,
          newStatus: newStatus,
          modifiedBy: user.userId,
        });
      } else {
        // Create new application
        const newApplicationId = await jobService.createJobApplication({
          postId: job.postId,
          userId: user.userId,
          createdBy: user.userId,
        });
        await jobService.updateJobApplication({
          applicationId: newApplicationId,
          newStatus: newStatus,
          modifiedBy: user.userId,
        });
      }

      // Refresh the data
      const applicationsResponse = await jobService.getUserApplications(
        user.userId,
        1,
        100
      );
      const applications = applicationsResponse.data.items || [];
      const applicationsMap = new Map<number, UserJobApplicationResponse>();
      applications.forEach((app) => {
        if (app.postId) {
          applicationsMap.set(app.postId, app);
        }
      });

      setJobsWithApplications((prevJobs) =>
        prevJobs.map((prevJob) => {
          const application = applicationsMap.get(prevJob.postId);
          return {
            ...prevJob,
            applicationStatus: application?.status,
            applicationId: application?.applicationId,
          };
        })
      );
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  const filterOptions: JobFilterOptions = {
    type: [
      { value: "all", label: "All Types" },
      { value: "FullTime", label: "Full-time" },
      { value: "PartTime", label: "Part-time" },
      { value: "Contract", label: "Contract" },
      { value: "Internship", label: "Internship" },
    ],
    location: [
      { value: "all", label: "All Locations" },
      { value: "Remote", label: "Remote" },
      // Add more locations if needed
    ],
    experience: [
      { value: "all", label: "All Experience" },
      { value: "entry", label: "Entry Level" },
      { value: "mid", label: "Mid Level" },
      { value: "senior", label: "Senior Level" },
    ],
  };

  const handleFilterChange = (
    filterType: keyof SelectedFilters,
    value: string
  ) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const filteredJobs = jobsWithApplications.filter((job) => {
    const matchesSearch =
      job.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      selectedFilters.type === "all" || job.jobType === selectedFilters.type;

    // Add location and experience filtering when data is available
    // const matchesLocation = ...
    // const matchesExperience = ...

    return matchesSearch && matchesType;
  });
  
  const formatSalary = (job: JobWithApplication): string => {
    if (job.minSalary && job.maxSalary) {
      return `₹${job.minSalary.toLocaleString()} - ₹${job.maxSalary.toLocaleString()}`;
    }
    return `Salary not specified`;
  };
  
  const formatDate = (date: string | undefined): string => {
    if (!date) return '';
    const jobDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - jobDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-black">
            Find Your Dream Job
          </h1>
          <p className="text-gray-600 mt-2">
            Discover opportunities that match your skills and interests
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Badge variant="success" size="lg">
            {filteredJobs.length} jobs found
          </Badge>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <div className="space-y-6">
          {/* Search Bar */}
          <SearchBar
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            placeholder="Search jobs, companies, or skills..."
            className="w-full"
          />

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Type
              </label>
              <select
                value={selectedFilters.type}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("type", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen"
              >
                {filterOptions.type.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <select
                value={selectedFilters.location}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("location", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen"
              >
                {filterOptions.location.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience
              </label>
              <select
                value={selectedFilters.experience}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("experience", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen"
              >
                {filterOptions.experience.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() =>
                  setSelectedFilters({
                    type: "all",
                    location: "all",
                    experience: "all",
                    remote: "all",
                  })
                }
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div className="space-y-6">
        {filteredJobs.length > 0 ? (
          filteredJobs.map((job) => (
            <div key={job.postId} className="job-card group">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-primary-black group-hover:text-primary-lightGreen transition-colors duration-300">
                      {job.jobTitle}
                    </h3>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <span className="font-semibold text-primary-black">
                      {job.companyName}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm text-gray-500 mb-1">
                    {formatDate(job.createdOn)}
                  </div>
                  <div className="text-lg font-bold text-primary-lightGreen">
                    {formatSalary(job)}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-primary-teaGreen text-primary-black rounded-full text-sm font-medium">
                  {job.jobType}
                </span>
                {/* Add location and other tags when data is available */}
              </div>

              <p className="text-gray-600 mb-4 line-clamp-3">
                {job.jobDescription}
              </p>

              <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <Link
                    to={`/post/${job.postId}`}
                    className="text-primary-lightGreen hover:text-primary-black font-medium transition-colors duration-300"
                  >
                    View Details
                  </Link>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApplicationStatusChange(job, "Bookmarked")}
                    disabled={job.applicationStatus === "Bookmarked"}
                    variant={job.applicationStatus === "Bookmarked" ? "primary" : "outline"}
                  >
                    {job.applicationStatus === "Bookmarked" ? "Saved" : "Save Job"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleApplicationStatusChange(job, "Applied")}
                    disabled={job.applicationStatus === "Applied"}
                    className="group-hover:shadow-glow"
                  >
                    {job.applicationStatus === "Applied" ? "Applied" : "Apply Now"}
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No jobs found
            </h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your search criteria or filters
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setSelectedFilters({
                  type: "all",
                  location: "all",
                  experience: "all",
                  remote: "all",
                });
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Load More */}
      {filteredJobs.length > 0 && (
        <div className="text-center">
          <Button variant="outline" size="lg">
            Load More Jobs
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserJobs;
